import { useState } from "react";
import { Alert, Button, Card, Col, Descriptions, Row, Space, Spin, Tabs } from "antd";
import { DownloadOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { useParams, useSearchParams } from "react-router-dom";
import { Crumbs } from "../components/Crumbs";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { can as canDo } from "@audit-plan/shared";
import { useAuth } from "../auth/AuthContext";
import { ApiError, download } from "../api/client";
import { useSupplier, useSupplierLog } from "../api/suppliers";
import { useShowRisk } from "../api/plan";
import { ManualMark, Pill, Rating, RiskPill, fmtD, fmtN } from "../components/pills";
import { ManualValueModal, type ManualTarget } from "../components/supplier/ManualValueModal";
import { PageHead } from "../components/dashboard/Kpi";
import { HBar, Sparkline } from "../components/charts/Charts";
import { SupplierFormModal } from "../components/supplier/SupplierFormModal";
import { NcCapConsolidated } from "../components/supplier/NcCapConsolidated";
import { LogTimeline } from "../components/supplier/Timeline";
import { AuditFormModal } from "../components/audit/AuditFormModal";
import { AuditTable } from "../components/audit/AuditTable";
import { FactoryTable } from "../components/factory/FactoryTable";
import { FactoryDrawer } from "../components/factory/FactoryDrawer";
import { FactoryFormModal } from "../components/factory/FactoryFormModal";
import { VendorhubData } from "../components/factory/VendorhubData";
import { EvidencePanel } from "../components/evidence/EvidencePanel";
import { ActionDrawer } from "../components/action/ActionDrawer";
import { NcDrawer } from "../components/nc/NcDrawer";
import { palette } from "../theme";
import { PageActions } from "../components/FilterBar";
import { useIsMobile } from "../components/useIsMobile";
import type { AuditDto } from "@audit-plan/shared";

const big: React.CSSProperties = { fontSize: 28, fontWeight: 800 };
const eyebrow: React.CSSProperties = { fontSize: 11, letterSpacing: 0.8, textTransform: "uppercase", color: palette.muted, fontWeight: 700 };

export default function SupplierPage() {
  const { t } = useTranslation();
  const showRisk = useShowRisk();
  const { id = "" } = useParams();
  const { me, can } = useAuth();
  const isSup = me?.role === "supplier";
  const mobile = useIsMobile();
  const q = useSupplier(id);
  const [sp] = useSearchParams();
  const [tab, setTab] = useState(sp.get("tab") ?? "ov");
  const log = useSupplierLog(id, tab === "log");
  const [edit, setEdit] = useState(false);
  const [manual, setManual] = useState<ManualTarget | null>(null);
  const [audForm, setAudForm] = useState<{ parent?: AuditDto; factoryId?: string } | null>(null);
  const [factoryId, setFactoryId] = useState<string | null>(null);
  const [newFactory, setNewFactory] = useState(false);
  const [ncId, setNcId] = useState<string | null>(null);
  const [actId, setActId] = useState<string | null>(null);

  if (isSup && me?.supplierId !== id) return <Alert type="warning" showIcon message={t("notAllowed")} />;
  if (q.isLoading) return <Spin style={{ display: "block", margin: 48 }} />;
  if (q.error || !q.data) return <Alert type="error" showIcon message={q.error instanceof ApiError ? q.error.message : t("noResults")} />;
  const s = q.data;
  const acts = s.actions;
  const doneN = acts.filter((a) => a.status === "done" || a.status === "closed").length;
  const cp = acts.length ? Math.round((doneN / acts.length) * 100) : null;
  const spark = s.scoreHistory.filter((h) => h.total != null).sort((a, b) => (a.date < b.date ? -1 : 1)).map((h) => h.total as number);
  const openNc = s.ncs.filter((n) => n.status !== "closed").length;
  const lastClosed = [...s.audits].filter((a) => a.status === "closed" && !a.parentAuditId).sort((a, b) => ((a.actualDate ?? "") < (b.actualDate ?? "") ? 1 : -1))[0];
  const canAudit = can("audit.create");
  const year = dayjs().year();

  const overview = (
    <Row gutter={[16, 16]}>
      <Col xs={24} lg={14}>
        <Card title={t("a_masterData")} extra={can("supplier.edit") && !isSup ? <Button size="small" onClick={() => setEdit(true)}>{t("edit")}</Button> : null}>
          <Descriptions column={1} size="small" layout={mobile ? "vertical" : "horizontal"} styles={{ label: mobile ? {} : { width: 150 }, content: { overflowWrap: "anywhere" } }}>
            <Descriptions.Item label={t("sapCode")}><span style={{ fontFamily: "var(--ap-mono)" }}>{s.code}</span></Descriptions.Item>
            <Descriptions.Item label={t("name")}>{s.name}</Descriptions.Item>
            {!isSup && (!s.active || can("admin")) && (
              <Descriptions.Item label={t("act_state")}>
                <Space size={6} wrap>
                  {s.active ? <Pill tone="ok">{t("act_active")}</Pill> : <Pill tone="mute">{t("act_inactive")}</Pill>}
                  {!s.active && s.activeNote && <span style={{ opacity: 0.65 }}>{s.activeNote}</span>}
                  {can("admin") && <Button size="small" onClick={() => setManual({ kind: "active", entity: "suppliers", id: s.id, name: s.name, active: s.active })}>{s.active ? t("act_deactivate") : t("act_reactivate")}</Button>}
                </Space>
              </Descriptions.Item>
            )}
            {!isSup && (s.phaseOut || can("admin")) && (
              <Descriptions.Item label={t("po_status")}>
                <Space size={6} wrap>
                  {s.phaseOut ? <Pill tone="crit">{t("phaseout")}</Pill> : <span style={{ opacity: 0.65 }}>{t("ans_N")}</span>}
                  {/* phase-out a mano (AUD-F8.4): solo admin; quello di un audit zero tolerance non si toglie da qui */}
                  {can("admin") && !s.phaseOutAuditId && (
                    <Button size="small" danger={!s.phaseOut} onClick={() => setManual({ kind: "phaseout", supplierId: s.id, name: s.name, phaseOut: s.phaseOut })}>
                      {s.phaseOut ? t("po_unset") : t("po_set")}
                    </Button>
                  )}
                </Space>
              </Descriptions.Item>
            )}
            <Descriptions.Item label={t("vat")}><span style={{ fontFamily: "var(--ap-mono)" }}>{s.vat || "—"}</span></Descriptions.Item>
            <Descriptions.Item label={t("country")}>{s.country || "—"}</Descriptions.Item>
            <Descriptions.Item label={t("legalAddress")}><span data-sensitive>{s.legalAddress || "—"}</span></Descriptions.Item>
            {s.nameLocal && s.nameLocal !== s.name && <Descriptions.Item label={t("f_nameLocal")}>{s.nameLocal}</Descriptions.Item>}
            <Descriptions.Item label={t("f_factories")}>
              {s.factories.length ? (
                <a onClick={() => setTab("fac")}>{t("f_factoriesCount", { n: s.factories.length, c: [...new Set(s.factories.map((f) => f.country).filter(Boolean))].join(", ") || "—" })}</a>
              ) : (
                "—"
              )}
            </Descriptions.Item>
            {s.businessUnits.length > 0 && <Descriptions.Item label={t("f_bu")}>{s.businessUnits.join(", ")}</Descriptions.Item>}
            {s.brands.length > 0 && <Descriptions.Item label={t("f_brands")}>{s.brands.join(", ")}</Descriptions.Item>}
            <Descriptions.Item label={t("contact")}>
              <div data-sensitive>
                {s.contactName}
                <div style={{ opacity: 0.65 }}>{[s.contactEmail, s.contactPhone].filter(Boolean).join(" · ")}</div>
              </div>
            </Descriptions.Item>
            <Descriptions.Item label={t("category")}>{s.category || "—"}</Descriptions.Item>
            <Descriptions.Item label={t("sector")}>{s.sector || "—"}</Descriptions.Item>
            {showRisk && (
              <Descriptions.Item label={t("risk")}>
                <Space size={6} wrap>
                  <RiskPill r={s.risk} />
                  {s.riskManual && <ManualMark note={s.riskNote} />}
                  {can("admin") && <Button size="small" type="text" icon={<EditOutlined />} aria-label={t("m_setRisk")} onClick={() => setManual({ kind: "risk", supplierId: s.id, name: s.name, risk: s.risk })} />}
                </Space>
              </Descriptions.Item>
            )}
          </Descriptions>
        </Card>
        {!isSup && s.vendorhubData && <div style={{ marginTop: 16 }} data-sensitive><VendorhubData data={s.vendorhubData} /></div>}
        {!isSup && s.vendorContacts.length > 0 && (
          <Card size="small" title={t("f_vendorContacts")} style={{ marginTop: 16 }}>
            {s.vendorContacts.slice(0, 12).map((c) => (
              <div key={c.id} data-sensitive>
                <b>{c.name}</b>
                <span style={{ opacity: 0.65 }}> {[c.title || c.role, s.factories.find((f) => f.id === c.factoryId)?.name, c.email, c.phone].filter(Boolean).join(" · ")}</span>
              </div>
            ))}
            {s.vendorContacts.length > 12 && <div style={{ opacity: 0.6 }}>{t("f_moreContacts", { n: s.vendorContacts.length - 12 })}</div>}
          </Card>
        )}
        {!isSup && s.contacts.length > 0 && (
          <Card size="small" title={t("a_supplierUsers")} style={{ marginTop: 16 }}>
            {s.contacts.map((u) => (
              <div key={u.id} data-sensitive>
                <b>{u.name}</b> <span style={{ opacity: 0.65 }}>{u.email}</span>
              </div>
            ))}
          </Card>
        )}
      </Col>
      <Col xs={24} lg={10}>
        <div style={{ display: "grid", gap: 16 }}>
          <Card>
            <div style={eyebrow}>{t("rating")}</div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 6 }}>
              <Rating score={s.rating} override={s.ratingOverride} />
              <span style={big}>{fmtN(s.rating, 1)}</span>
              <span style={{ opacity: 0.6 }}>/ 100</span>
              {s.ratingManual && <ManualMark note={s.ratingNote} />}
              {can("admin") && (
                <Button size="small" type="text" icon={<EditOutlined />} aria-label={t("m_setRating")} onClick={() => setManual({ kind: "rating", entity: "suppliers", id: s.id, name: s.name, rating: s.rating, manual: s.ratingManual })} />
              )}
            </div>
            {spark.length > 1 && <div style={{ marginTop: 8 }}><Sparkline values={spark} /></div>}
          </Card>
          <Card>
            <div style={eyebrow}>{t("overallCap")}</div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginTop: 6 }}>
              <span style={big}>{cp == null ? "—" : `${cp}%`}</span>
              <span style={{ opacity: 0.6 }}>{doneN}/{acts.length}</span>
            </div>
            <div style={{ marginTop: 8 }}><HBar pct={cp ?? 0} tone="ok" /></div>
          </Card>
          {!isSup && showRisk && (
            <Card>
              <div style={eyebrow}>{t("visitsPlanned")} {year}</div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginTop: 6 }}>
                <span style={big}>{s.visitsPlannedThisYear ?? 0}</span>
                <span style={{ opacity: 0.6 }}>/ {s.visitsRequired ?? 0} {t("a_requiredByRisk")}</span>
              </div>
            </Card>
          )}
        </div>
      </Col>
    </Row>
  );

  const items = [
    { key: "ov", label: t("overview"), children: overview },
    {
      key: "fac",
      label: `${t("f_factories")} (${s.factories.length})`,
      children: (
        <div style={{ display: "grid", gap: 12 }}>
          {can("supplier.edit") && !isSup && (
            <div><Button icon={<PlusOutlined />} onClick={() => setNewFactory(true)}>{t("f_new")}</Button></div>
          )}
          <Card styles={{ body: { padding: 0 } }}><FactoryTable factories={s.factories} onOpen={(f) => setFactoryId(f.id)} /></Card>
        </div>
      ),
    },
    {
      key: "aud",
      label: `${t("audits")} (${s.audits.length})`,
      children: (
        <div style={{ display: "grid", gap: 12 }}>
          {canAudit && lastClosed && !s.phaseOut && (
            <div><Button onClick={() => setAudForm({ parent: lastClosed })}>{t("newFollowup")} ({lastClosed.code})</Button></div>
          )}
          <Card styles={{ body: { padding: 0 } }}><AuditTable audits={s.audits} withFactory={s.factories.length > 1} /></Card>
        </div>
      ),
    },
    { key: "nc", label: `${t("ncCap")} (${openNc})`, children: <NcCapConsolidated ncs={s.ncs} actions={s.actions} onNc={setNcId} onAction={setActId} /> },
    {
      key: "ev",
      label: `${t("evidence")} (${s.evidence.length})`,
      children: <EvidencePanel evidence={s.evidence} ctx={{ supplierId: id, target: { type: "supplier", id } }} canUpload={canDo(me, "evidence.upload", { supplierId: id })} showAudit onChanged={() => void q.refetch()} />,
    },
    { key: "log", label: t("history"), children: <Card>{log.isLoading ? <Spin /> : <LogTimeline logs={log.data ?? []} />}</Card> },
  ];

  return (
    <div>
      <PageHead
        title={isSup ? `${t("myCompany")} · ${s.name}` : s.name}
        crumb={isSup ? undefined : <Crumbs items={[{ label: t("suppliers"), to: "/suppliers" }, { label: s.name }]} />}
        actions={
          <PageActions
            items={[
              { key: "pdf", label: t("exportPdf"), icon: <DownloadOutlined />, onClick: () => void download(`/suppliers/${id}/dossier.pdf`) },
              { key: "xlsx", label: t("exportXlsx"), icon: <DownloadOutlined />, onClick: () => void download(`/suppliers/${id}/dossier.xlsx`) },
              { key: "new", label: t("newAudit"), icon: <PlusOutlined />, primary: true, hidden: !(canAudit && !s.phaseOut), onClick: () => setAudForm({}) },
            ]}
          />
        }
      />
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", fontSize: 13, color: palette.muted, marginBottom: 14 }}>
        <span style={{ fontFamily: "var(--ap-mono)" }}>{s.code}</span>·<span>{s.category}</span>·{showRisk && <RiskPill r={s.risk} />}<Rating score={s.rating} override={s.ratingOverride} />
      </div>
      {s.phaseOut && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16, borderColor: palette.crit, overflowWrap: "anywhere" }}
          message={<><Pill tone="crit">{t("phaseout")}</Pill> {s.phaseOutReason}</>}
          description={`${t("po_since", { d: fmtD(s.phaseOutAt) })}${s.phaseOutAuditId ? ` · ${t("po_fromAudit", { code: s.audits.find((a) => a.id === s.phaseOutAuditId)?.code ?? "" })}` : ` · ${t("po_manual")}`}`}
        />
      )}
      <Tabs activeKey={tab} onChange={setTab} items={items} tabBarGutter={mobile ? 18 : undefined} />
      <SupplierFormModal open={edit} supplier={s} onClose={() => setEdit(false)} />
      <ManualValueModal target={manual} onClose={() => setManual(null)} />
      {audForm && <AuditFormModal open onClose={() => setAudForm(null)} supplierId={id} factoryId={audForm.factoryId} parent={audForm.parent} onSaved={() => void q.refetch()} />}
      <FactoryDrawer factoryId={factoryId} supplierId={id} onClose={() => setFactoryId(null)} onNewAudit={s.phaseOut ? undefined : (fid) => { setFactoryId(null); setAudForm({ factoryId: fid }); }} />
      <FactoryFormModal open={newFactory} supplierId={id} onClose={() => setNewFactory(false)} onSaved={() => void q.refetch()} />
      <NcDrawer ncId={ncId} onClose={() => setNcId(null)} />
      <ActionDrawer actionId={actId} onClose={() => setActId(null)} />
    </div>
  );
}
